import { ThreadStoreService } from "../storage/ChatThreadStore.js";
import { ThreadIndexService } from "../storage/ChatThreadIndex.js";
import { ContactStore } from "../storage/ChatContactStore.js";
import { ConnectRequestStore } from "../storage/ConnectRequestStore.js";
import { GroupStore } from "../storage/ChatGroupStore.js";
import { ChannelStore } from "../storage/ChatChannelStore.js";
import { DeviceFanoutCacheStore } from "../storage/DeviceFanoutCacheStore.js";
import { ChatServerBus } from "./ChatServerBus.js";
import { ChatBridge } from "../transport/ChatBridge.js";
import { InboundDepositPipeline } from "../runtime/InboundDepositPipeline.js";
import { ProcessedDepositLog } from "../inbox/ProcessedDepositLog.js";
import { InboundApplyOutbox } from "../inbox/InboundApplyOutbox.js";
import { ServerRuntimeService } from "../services/ServerRuntimeService.js";
import { ServerSessionService } from "../services/ServerSessionService.js";
import { ServerThreadsService } from "../services/ServerThreadsService.js";
import { ServerMessagesService } from "../services/ServerMessagesService.js";
import { ServerContactsService } from "../services/ServerContactsService.js";
import { ServerGroupsService } from "../services/ServerGroupsService.js";
import { ServerChannelsService } from "../services/ServerChannelsService.js";
import { ServerInvitesService } from "../services/ServerInvitesService.js";
import { ServerConnectionService } from "../services/ServerConnectionService.js";
import { ServerEventService } from "../services/ServerEventService.js";
import { ServerPeerLinkProtocolService } from "../services/ServerPeerLinkProtocolService.js";
import { ServerDeviceSetService } from "../services/ServerDeviceSetService.js";
import { ServerAccountMutationService } from "../services/ServerAccountMutationService.js";
import { ServerAuthorityPublicationService } from "../services/ServerAuthorityPublicationService.js";
import { ServerAccountStateSyncService } from "../services/ServerAccountStateSyncService.js";
import { ServerFileTransferService } from "../services/ServerFileTransferService.js";
import { ServerProfileService } from "../services/ServerProfileService.js";
import { InboxCatchupService } from "../services/InboxCatchupService.js";
import { GlobalGroupLookup } from "../services/GlobalGroupLookup.js";
import { ServerDeviceLinkService } from "../services/ServerDeviceLinkService.js";

export class ChatServerApp {
  #started;
  #services;
  #clock;
  #ownerAccountId;
  #storageProvider;
  #bridge;

  constructor({
    identity,
    uplinks,
    storageProvider,
    ownerAccountId,
    clock = () => Date.now(),
    sdk = null,
    peerLinkService = null,
    inboxClaimant = null,
    expectedNodePublicKeyB64 = "",
    accountAuthority = null,
    accountIdentityDhKeyPair = null,
    wsFactory = null,
    linksServiceFactory = null,
    deviceLinkServiceFactory = null,
    logger = console,
  } = {}) {
    if (!Array.isArray(uplinks) || uplinks.length === 0) {
      throw new Error("ChatServerApp requires uplinks");
    }
    if (!storageProvider || typeof storageProvider.getKeyValueStore !== "function") {
      throw new Error("ChatServerApp requires storageProvider");
    }
    if (typeof ownerAccountId !== "string" || ownerAccountId.trim().length === 0) {
      throw new Error("ChatServerApp requires ownerAccountId");
    }
    this.#started = false;
    this.#clock = clock;
    this.#ownerAccountId = ownerAccountId.trim();
    this.#storageProvider = storageProvider;
    this.bus = new ChatServerBus({
      config: { identity, uplinks, ownerAccountId: this.#ownerAccountId },
      logger,
    });
    this.#createStores(storageProvider);
    // Expose chat-server's persistent inbox claim to handlers via the bus
    // runtime — chat-server uses this inboxId for invites/deposits instead
    // of the SDK session's ephemeral assignment.
    this.bus.runtime.inboxClaimant = inboxClaimant;
    // Account-key signer/verifier (REZ-2): the same authority that signs invite
    // envelopes, used here to sign + verify membership-consent proofs on group
    // ops. bootstrapChatServer passes the real authority; tests that drive the
    // group services directly install a double on bus.runtime.accountAuthority.
    this.bus.runtime.accountAuthority = accountAuthority;
    // S10: the account identity-DH keypair (B-dh) — the delegation bundle a
    // device-link ceremony seals must ship the PAIR. Primary boots thread it
    // from the vault via bootstrapChatServer; null on legacy/web vaults
    // (deviceLink.start fails loud with a typed error).
    this.bus.runtime.accountIdentityDhKeyPair = accountIdentityDhKeyPair;
    this.#createServices({
      identity,
      uplinks,
      clock,
      sdk,
      peerLinkService,
      inboxClaimant,
      expectedNodePublicKeyB64,
      wsFactory,
      linksServiceFactory,
      deviceLinkServiceFactory,
      logger,
    });
    this.#bridge = new ChatBridge({
      bus: this.bus,
      ownerAccountId: this.#ownerAccountId,
      logger,
    });
    this.bus.transport.bridge = this.#bridge;
  }

  get bridge() {
    return this.#bridge;
  }

  get started() {
    return this.#started;
  }

  get ownerAccountId() {
    return this.#ownerAccountId;
  }

  get storageProvider() {
    return this.#storageProvider;
  }

  get sdk() {
    return this.bus.runtime && this.bus.runtime.sdk ? this.bus.runtime.sdk : null;
  }

  get threadStore() {
    return this.bus.stores.threadStore;
  }

  get threadIndex() {
    return this.bus.stores.threadIndex;
  }

  get contactStore() {
    return this.bus.stores.contactStore;
  }

  get groupStore() {
    return this.bus.stores.groupStore;
  }

  on(eventName, handler) {
    return this.bus.on(eventName, handler);
  }

  off(eventName, handler) {
    this.bus.off(eventName, handler);
  }

  async start() {
    if (this.#started) return;
    this.#started = true;
    this.bus.emit("server.starting", {});
    await this.bus.services.runtime.connect();
    for (const service of this.#services) {
      if (service === this.bus.services.runtime) continue;
      if (service && typeof service.start === "function") {
        await service.start();
      }
    }
    this.bus.resolveReady.server();
    this.bus.emit("server.ready", {});
  }

  async stop() {
    if (!this.#started) return;
    this.bus.emit("server.stopping", {});
    const list = [...this.#services].reverse();
    for (const service of list) {
      if (service && typeof service.stop === "function") {
        await service.stop();
      }
    }
    this.#started = false;
  }

  #createStores(storageProvider) {
    this.bus.stores.storageProvider = storageProvider;
    this.bus.stores.threadStore = new ThreadStoreService({
      storageProvider,
      ownerAccountId: this.#ownerAccountId,
      clock: this.#clock,
    });
    this.bus.stores.threadIndex = new ThreadIndexService({
      storageProvider,
      ownerAccountId: this.#ownerAccountId,
      threadStore: this.bus.stores.threadStore,
      clock: this.#clock,
    });
    this.bus.stores.contactStore = new ContactStore({
      storageProvider,
      clock: this.#clock,
    });
    this.bus.stores.connectRequestStore = new ConnectRequestStore({
      storageProvider,
      clock: this.#clock,
    });
    this.bus.stores.groupStore = new GroupStore({
      storageProvider,
      clock: this.#clock,
    });
    this.bus.stores.channelStore = new ChannelStore({
      storageProvider,
      clock: this.#clock,
    });
    // Durable per-(messageId, peerDeviceId) sealed-ciphertext cache so a send
    // retry — including one after a sender restart — replays identical bytes
    // instead of re-encrypting (Audit R3 #4). Only exercised on the gated
    // per-device fan-out path; harmless (untouched) on fs / DO-relay nodes.
    this.bus.stores.deviceFanoutStore = new DeviceFanoutCacheStore({
      storageProvider,
      clock: this.#clock,
    });
    this.bus.stores.globalGroupLookup = new GlobalGroupLookup({
      groupStore: this.bus.stores.groupStore,
    });
  }

  #createServices({
    identity,
    uplinks,
    clock,
    sdk,
    peerLinkService,
    inboxClaimant,
    expectedNodePublicKeyB64,
    wsFactory,
    linksServiceFactory,
    deviceLinkServiceFactory,
    logger,
  }) {
    const services = {
      runtime: new ServerRuntimeService({
        bus: this.bus,
        identity,
        uplinks,
        sdk,
        peerLinkService,
        inboxClaimant,
        expectedNodePublicKeyB64,
        wsFactory,
        logger,
      }),
      session: new ServerSessionService({
        bus: this.bus,
        storageProvider: this.#storageProvider,
        ownerAccountId: this.#ownerAccountId,
        logger,
      }),
      threads: new ServerThreadsService({
        bus: this.bus,
        threadStore: this.bus.stores.threadStore,
        threadIndex: this.bus.stores.threadIndex,
        contactStore: this.bus.stores.contactStore,
        groupStore: this.bus.stores.groupStore,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      messages: new ServerMessagesService({
        bus: this.bus,
        threadStore: this.bus.stores.threadStore,
        threadIndex: this.bus.stores.threadIndex,
        groupStore: this.bus.stores.groupStore,
        deviceFanoutStore: this.bus.stores.deviceFanoutStore,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      contacts: new ServerContactsService({
        bus: this.bus,
        contactStore: this.bus.stores.contactStore,
        connectRequestStore: this.bus.stores.connectRequestStore,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      groups: new ServerGroupsService({
        bus: this.bus,
        groupStore: this.bus.stores.groupStore,
        threadStore: this.bus.stores.threadStore,
        threadIndex: this.bus.stores.threadIndex,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      channels: new ServerChannelsService({
        bus: this.bus,
        channelStore: this.bus.stores.channelStore,
        groupStore: this.bus.stores.groupStore,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      invites: new ServerInvitesService({
        bus: this.bus,
        logger,
      }),
      connection: new ServerConnectionService({
        bus: this.bus,
        logger,
      }),
      fileTransfer: new ServerFileTransferService({
        bus: this.bus,
        storageProvider: this.#storageProvider,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      profile: new ServerProfileService({
        bus: this.bus,
        contactStore: this.bus.stores.contactStore,
        threadStore: this.bus.stores.threadStore,
        storageProvider: this.#storageProvider,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      events: new ServerEventService({
        bus: this.bus,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      peerLinkProtocol: new ServerPeerLinkProtocolService({
        bus: this.bus,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      // S2.5 Slice 5: per-device E2EE device-set publish/resolve/respond. A no-op
      // unless this account runs per-device sessions (a device key is present —
      // desktop with a v2 keystore). The E6 fan-out gate lives in the sender
      // (ServerMessagesService), not here.
      deviceSet: new ServerDeviceSetService({
        bus: this.bus,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      // S11: serialized device add/revoke + revocation propagation. Reuses the
      // device-set republish + a public authority-state record. E6-independent.
      accountMutation: new ServerAccountMutationService({
        bus: this.bus,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      // P1#3 leaf 5b: drains the home's authority-state publication outbox — the
      // node enqueues the obligation in the mutation's own transaction but cannot
      // discharge it, since the record is ACCOUNT-signed. Driven on demand via the
      // `authority-publication` / `drain` directive; it does not yet replace the
      // inline publish in accountMutation#propagate (separate cutover).
      authorityPublication: new ServerAuthorityPublicationService({
        bus: this.bus,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
      // S14: cross-device account-state sync. Fans relationship-graph deltas
      // (contacts, direct threads) to sibling device inboxes so a sibling that
      // never took part in an invite surfaces (and can reply to) a peer's
      // fanned-out message. No-op unless this account runs per-device sessions.
      accountStateSync: new ServerAccountStateSyncService({
        bus: this.bus,
        storageProvider: this.#storageProvider,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      }),
    };
    if (typeof linksServiceFactory === "function") {
      const links = linksServiceFactory({
        bus: this.bus,
        storageProvider: this.#storageProvider,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      });
      if (!links || typeof links !== "object") {
        throw new Error("ChatServerApp linksServiceFactory returned invalid service");
      }
      services.links = links;
    }
    const deviceLink = typeof deviceLinkServiceFactory === "function"
      ? deviceLinkServiceFactory({
        bus: this.bus,
        storageProvider: this.#storageProvider,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      })
      : new ServerDeviceLinkService({
        bus: this.bus,
        storageProvider: this.#storageProvider,
        ownerAccountId: this.#ownerAccountId,
        clock,
        logger,
      });
    if (!deviceLink || typeof deviceLink !== "object") {
      throw new Error("ChatServerApp deviceLinkServiceFactory returned invalid service");
    }
    services.deviceLink = deviceLink;
    // The single serialized inbound path. Both the live SDK push
    // (MailboxPushBridge) and the catch-up drain (InboxCatchupService) feed
    // deposits through this one pipeline so each is fully applied before the
    // next — no fire-and-forget emit, no ordering race. Not a lifecycle service
    // (no start/stop); registered on the bus for the bridge + catch-up to reach.
    // Persisted (mailbox,event) dedup shared by the pipeline (check + mark) and
    // the catch-up drain (prune past the cursor). Stops a cold-boot drain from
    // re-decrypting a deposit already consumed via the live push path — a
    // re-decrypt fails the advanced double ratchet and used to swallow the next
    // (genuinely new) offline message.
    const processedLog = new ProcessedDepositLog({ kvStore: this.#storageProvider.getKeyValueStore(null) });
    // Durable post-decrypt apply-outbox (audit P1.1): a decrypted message is
    // staged here before the cursor/buffer ack can advance, so an application
    // failure is retried instead of silently losing the only ciphertext.
    const applyOutbox = new InboundApplyOutbox({ kvStore: this.#storageProvider.getKeyValueStore(null) });
    const inboundPipeline = new InboundDepositPipeline({
      peerLinkProtocol: services.peerLinkProtocol,
      events: services.events,
      processedLog,
      outbox: applyOutbox,
      logger,
    });
    // Catchup is only meaningful when an inbox is claimed (production). In
    // unit-test paths that wire ChatServerApp without an inboxClaimant the
    // SDK push bridge is also not engaged, so nothing to drain. Added last
    // so its start() runs after the live-push subscribers have wired up.
    if (inboxClaimant) {
      services.inboxCatchup = new InboxCatchupService({
        bus: this.bus,
        inboxClaimant,
        inboundPipeline,
        processedLog,
        logger,
      });
    }
    // Register the pipeline as a lifecycle participant so its periodic retry-sweep
    // timer starts/stops with the app (start() after the live-push subscribers,
    // stop() clears the timer on shutdown).
    services.inboundPipeline = inboundPipeline;
    Object.assign(this.bus.services, services);
    this.#services = Object.values(services);
  }
}
